import { c as _c } from "react/compiler-runtime";
import { ImageOff, Play, X } from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { useState } from 'react';
import { MediaGrid } from '@/components/tweet/TweetMediaGrid';
import { TweetVideoPlayer } from '@/components/tweet/TweetVideoPlayer';
import { Button } from '@/components/ui/button';
import { useIsClient } from '@/hooks/useIsClient';
import { isProductionRuntime } from '@/lib/runtime-flags';
import { constructPreviewUrl, withPhotoSize } from '@/lib/twitter/media-utils';
import { cn } from '@/lib/utils';
import type { UIMediaItem } from '@/types/ui-media';
import { mediaLogger } from './logger';
import { MediaViewerPortal } from './MediaViewerPortal';
import { PhotoImage } from './PhotoImage';
import { PhotoOverlay } from './PhotoOverlay';
import type { TweetMediaProperties } from './types';
import { useMediaViewerState } from './useMediaViewerState';
import { usePhotoLoadTracker } from './usePhotoLoadTracker';
import { useTweetMediaItems } from './useTweetMediaItems';
import { selectBestVideoVariant } from './VideoUtils';
const INLINE_ASPECT_RATIO = 16 / 9;
const loggedMissingVideoSources = new Set<string>();
export function __resetLoggedMissingVideoSourcesForTests(): void {
  if (!isProductionRuntime()) {
    loggedMissingVideoSources.clear();
  }
}
function getMaxHeight(aspectRatio: number, totalItems: number) {
  if (totalItems > 1) {
    return;
  }
  if (aspectRatio > 2) {
    return '300px';
  }
  if (aspectRatio > 1.5) {
    return '400px';
  }
  if (aspectRatio > 0.8) {
    return '500px';
  }
  return '600px';
}
function getAspectRatio(media: UIMediaItem) {
  if (media.width && media.height) {
    return media.width / media.height;
  }
  return INLINE_ASPECT_RATIO;
}
function isVideo(media: UIMediaItem) {
  return media.type === 'video' || media.type === 'animated_gif';
}
function getPhotoFailureKey(media: UIMediaItem, index: number): string {
  if (media.mediaKey) {
    return media.mediaKey;
  }
  return `index-${index}`;
}
export function TweetMedia(t0) {
  const $ = _c(41);
  const {
    mediaItems,
    onViewerStateChange
  } = t0;
  const items = useTweetMediaItems(mediaItems);
  const isMounted = useIsClient();
  const {
    registerOverlay,
    registerImage,
    markLoaded
  } = usePhotoLoadTracker();
  const [failedPhotoKeys, setFailedPhotoKeys] = useState(_temp);
  const [failedVideoKeys, setFailedVideoKeys] = useState(_temp2);
  let t1;
  if ($[0] !== items || $[1] !== onViewerStateChange) {
    t1 = onViewerStateChange ? {
      mediaItems: items,
      onViewerStateChange
    } : {
      mediaItems: items
    };
    $[0] = items;
    $[1] = onViewerStateChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const mediaViewerParameters = t1;
  const {
    selectedIndex,
    isDialogOpen,
    inlineVideoIndex,
    videoReady,
    openPhoto,
    playInlineVideo,
    stopInlineVideo,
    closeViewer,
    goToPrevious,
    goToNext,
    setVideoReady
  } = useMediaViewerState(mediaViewerParameters);
  if (items.length === 0) {
    return null;
  }
  let t2;
  if ($[3] !== failedPhotoKeys || $[4] !== isDialogOpen || $[5] !== items || $[6] !== openPhoto || $[7] !== playInlineVideo || $[8] !== selectedIndex) {
    const handleMediaActivation = index => {
      const media = items[index];
      mediaLogger.debug("TweetMedia - Click registered", {
        index,
        mediaType: media?.type,
        hasUrl: Boolean(media?.url),
        url: media?.url,
        disabled: !media?.url,
        currentState: {
          isDialogOpen,
          selectedIndex
        }
      });
      if (!media) {
        return;
      }
      const failureKey = getPhotoFailureKey(media, index);
      if (failedPhotoKeys.has(failureKey)) {
        return;
      }
      if (isVideo(media)) {
        playInlineVideo(index);
        return;
      }
      openPhoto(index);
    };
    t2 = (index_0, event) => {
      event.stopPropagation();
      event.preventDefault();
      handleMediaActivation(index_0);
    };
    $[3] = failedPhotoKeys;
    $[4] = isDialogOpen;
    $[5] = items;
    $[6] = openPhoto;
    $[7] = playInlineVideo;
    $[8] = selectedIndex;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleMediaClick = t2;
  let t3;
  if ($[10] !== failedPhotoKeys || $[11] !== handleMediaClick || $[12] !== items.length || $[13] !== markLoaded || $[14] !== registerImage || $[15] !== registerOverlay) {
    t3 = (media_0, index_1, aspectRatio, forceAspect) => {
      if (!media_0.url) {
        mediaLogger.warn("TweetMedia - Media item missing URL", {
          media: media_0
        });
        return null;
      }
      const sizedUrl = withPhotoSize(media_0.url, "large");
      const failureKey_0 = getPhotoFailureKey(media_0, index_1);
      const loadFailed = failedPhotoKeys.has(failureKey_0);
      const maxHeight = getMaxHeight(aspectRatio, items.length);
      const baseImageStyle = forceAspect ? undefined : {
        aspectRatio,
        ...(maxHeight ? {
          maxHeight
        } : {})
      };
      let imageStyle = baseImageStyle;
      if (loadFailed) {
        imageStyle = baseImageStyle ? {
          ...baseImageStyle,
          display: "none"
        } : {
          display: "none"
        };
      }
      const handleButtonClick = loadFailed ? undefined : event_0 => {
        handleMediaClick(index_1, event_0);
      };
      const handlePhotoClick = loadFailed ? undefined : event_1 => {
        handleMediaClick(index_1, event_1);
      };
      return <button type="button" className={cn("relative h-full w-full cursor-pointer overflow-hidden bg-muted text-left", !forceAspect && "rounded-lg", loadFailed && "cursor-default")} data-no-conversation={true} onClick={handleButtonClick} disabled={loadFailed} aria-disabled={loadFailed}><PhotoOverlay index={index_1} registerOverlay={registerOverlay} className="absolute inset-0 bg-muted transition-opacity duration-300 data-[loaded=true]:opacity-0" /><PhotoImage index={index_1} src={sizedUrl} alt={media_0.altText || "Tweet image"} width={typeof media_0.width === "number" ? media_0.width : null} height={typeof media_0.height === "number" ? media_0.height : null} className={cn("w-full cursor-pointer object-cover transition-all duration-200 hover:opacity-95", !forceAspect && "rounded-lg", forceAspect ? "h-full" : "h-auto", "data-[loaded=false]:blur-sm data-[loaded=false]:scale-105", loadFailed && "hidden")} {...imageStyle ? {
          style: imageStyle
        } : {}} registerImage={registerImage} markLoaded={markLoaded} {...handlePhotoClick ? {
          onClick: handlePhotoClick
        } : {}} onError={() => {
          setFailedPhotoKeys(previous => {
            if (previous.has(failureKey_0)) {
              return previous;
            }
            const next = new Set(previous);
            next.add(failureKey_0);
            return next;
          });
          mediaLogger.warn("TweetMedia - Image failed to load", {
            url: sizedUrl,
            mediaKey: media_0.mediaKey
          });
        }} />{loadFailed && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground"><ImageOff className="h-6 w-6" aria-hidden={true} /><span className="text-xs font-medium">Image unavailable</span></div>}</button>;
    };
    $[10] = failedPhotoKeys;
    $[11] = handleMediaClick;
    $[12] = items.length;
    $[13] = markLoaded;
    $[14] = registerImage;
    $[15] = registerOverlay;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const renderPhotoItem = t3;
  let t4;
  if ($[17] !== failedVideoKeys || $[18] !== handleMediaClick || $[19] !== inlineVideoIndex || $[20] !== items.length || $[21] !== setVideoReady || $[22] !== stopInlineVideo || $[23] !== videoReady) {
    t4 = (media_1, index_2, aspectRatio_0, forceAspect_0) => {
      const maxHeight_0 = getMaxHeight(aspectRatio_0, items.length);
      const containerStyle = forceAspect_0 ? undefined : {
        aspectRatio: aspectRatio_0,
        ...(maxHeight_0 ? {
          maxHeight: maxHeight_0
        } : {})
      };
      const isPlayingInline = inlineVideoIndex === index_2;
      const videoUrl = selectBestVideoVariant(media_1);
      const previewUrl = media_1.previewImageUrl ?? undefined;
      const synthesizedPreview = previewUrl && media_1.mediaKey ? previewUrl === constructPreviewUrl(media_1.mediaKey) : false;
      const failureKey_1 = getPhotoFailureKey(media_1, index_2);
      const loadFailed_0 = failedVideoKeys.has(failureKey_1);
      const resolvedPreviewUrl = synthesizedPreview ? undefined : previewUrl;
      const hasPlayableVideo = Boolean(videoUrl);
      if (!hasPlayableVideo) {
        const key = media_1.mediaKey ?? `${media_1.type}:${media_1.url ?? "unknown"}`;
        if (!loggedMissingVideoSources.has(key)) {
          loggedMissingVideoSources.add(key);
          mediaLogger.warn("TweetMedia - Missing playable video source", {
            mediaKey: media_1.mediaKey ?? null,
            url: media_1.url ?? null,
            variantCount: Array.isArray(media_1.variants) ? media_1.variants.length : 0
          });
        }
      }
      return <div className={cn("relative h-full w-full overflow-hidden", !forceAspect_0 && "rounded-lg")} data-no-conversation={true}>{resolvedPreviewUrl && !loadFailed_0 ? <Image src={resolvedPreviewUrl} alt={media_1.altText || "Video thumbnail"} width={media_1.width ?? 400} height={media_1.height ?? 300} className={cn(`w-full ${forceAspect_0 ? "h-full" : "h-auto"} object-cover transition-opacity duration-150`, isPlayingInline && videoReady ? "opacity-0" : "opacity-100")} style={containerStyle} loading="lazy" draggable={false} unoptimized={true} onError={() => {
          setFailedVideoKeys(previous_0 => {
            if (previous_0.has(failureKey_1)) {
              return previous_0;
            }
            const next_0 = new Set(previous_0);
            next_0.add(failureKey_1);
            return next_0;
          });
          mediaLogger.warn("TweetMedia - Video preview failed to load", {
            url: resolvedPreviewUrl,
            mediaKey: media_1.mediaKey ?? null
          });
        }} /> : <div className={`flex w-full items-center justify-center ${forceAspect_0 ? "h-full" : "h-auto"} bg-muted`} style={containerStyle}><span className="text-muted-foreground">Video</span></div>}{isPlayingInline && videoUrl && <div className="absolute inset-0"><TweetVideoPlayer src={videoUrl} playing={true} controls={media_1.type === "video"} loop={media_1.type === "animated_gif"} muted={media_1.type === "animated_gif"} playsInline={true} width="100%" height="100%" onReady={() => {
            setVideoReady(true);
          }} onStart={() => {
            setVideoReady(true);
          }} onPlay={() => {
            setVideoReady(true);
          }} style={{
            opacity: videoReady ? 1 : 0,
            transition: "opacity 150ms ease-in",
            objectFit: "cover"
          }} /></div>}{(() => {
          if (isPlayingInline) {
            return <button type="button" aria-label="Close video" className="absolute top-2 right-2 z-layer-surface rounded-full bg-black/60 p-1 text-white hover:bg-black/70" onClick={() => {
              stopInlineVideo();
            }}><X className="h-4 w-4" /></button>;
          }
          if (!hasPlayableVideo) {
            return null;
          }
          return <div className="absolute inset-0 flex items-center justify-center bg-black/20" role="presentation" data-testid="tweet-media-play-overlay"><Button variant="secondary" size="lg" className="rounded-full bg-card/90 text-foreground hover:bg-card" onClick={event_2 => {
              handleMediaClick(index_2, event_2);
            }} disabled={!videoUrl} aria-label="Play video"><Play className="ml-1 h-6 w-6" /></Button></div>;
        })()}{media_1.type === "animated_gif" && <div className="absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-xs text-white">GIF</div>}{media_1.durationMs && <div className="absolute bottom-2 right-2 rounded bg-black/70 px-2 py-1 text-xs text-white">{Math.floor(media_1.durationMs / 1000)}s</div>}</div>;
    };
    $[17] = failedVideoKeys;
    $[18] = handleMediaClick;
    $[19] = inlineVideoIndex;
    $[20] = items.length;
    $[21] = setVideoReady;
    $[22] = stopInlineVideo;
    $[23] = videoReady;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  const renderVideoItem = t4;
  let t5;
  if ($[25] !== renderPhotoItem || $[26] !== renderVideoItem) {
    t5 = (media_2, index_3, forceAspectRatio) => {
      const aspectRatio_1 = getAspectRatio(media_2);
      if (media_2.type === "photo") {
        return renderPhotoItem(media_2, index_3, aspectRatio_1, forceAspectRatio);
      }
      if (isVideo(media_2)) {
        return renderVideoItem(media_2, index_3, aspectRatio_1, forceAspectRatio);
      }
      return null;
    };
    $[25] = renderPhotoItem;
    $[26] = renderVideoItem;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const renderSingleMedia = t5;
  let t6;
  if ($[28] !== items || $[29] !== renderSingleMedia) {
    t6 = <MediaGrid mediaItems={items} renderSingleMedia={renderSingleMedia} />;
    $[28] = items;
    $[29] = renderSingleMedia;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const t7 = Boolean(isMounted && isDialogOpen && "window" in globalThis && selectedIndex !== undefined && items[selectedIndex] && items[selectedIndex].type === "photo");
  let t8;
  if ($[31] !== closeViewer || $[32] !== goToNext || $[33] !== goToPrevious || $[34] !== items || $[35] !== selectedIndex || $[36] !== t7) {
    t8 = <MediaViewerPortal open={t7} mediaItems={items} selectedIndex={selectedIndex} onClose={closeViewer} onPrev={goToPrevious} onNext={goToNext} resolveVideoUrl={selectBestVideoVariant} />;
    $[31] = closeViewer;
    $[32] = goToNext;
    $[33] = goToPrevious;
    $[34] = items;
    $[35] = selectedIndex;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== t6 || $[39] !== t8) {
    t9 = <div className="mt-3" data-no-conversation={true}>{t6}{t8}</div>;
    $[38] = t6;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
}
function _temp2() {
  return new Set();
}
function _temp() {
  return new Set();
}
export default TweetMedia;